import { FC, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { FieldValues, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import {
  fetchCaptcha,
  signUpSelector,
  resetCompanyDetails,
  submitCompanyDetails
} from 'store/signupState';
import { authSelector } from 'store/authState';
import { localizationSelector } from 'store/localizationState';
import * as routes from 'router/routes';
import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { allKeysAreEmptyStrings } from 'utils/objectUtils';
import { getBrowserCountryCode } from 'utils/localizationUtils';
import companyIcon from 'assets/vectors/company.svg';

import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import SelectField from 'components/UI/SelectField';
import AnimatedFormGroup from 'components/UI/AnimatedFormGroup';
import PhoneNumberField from 'components/UI/PhoneNumberField';
import BackButton from 'components/UI/Buttons/BackButton';
import PageHeader from 'components/UI/Page/PageHeader';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import SeoHelmet from 'components/fragments/SeoHelmet';

import { Form, ButtonContainer } from '../styled';

export const CompanyDetailsPage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { session, hasLoggedIn } = useAppSelector(authSelector);
  const { data, isCredentialsCompleted, error } =
    useAppSelector(signUpSelector);
  const { languageCode = 'en', countryOptions } =
    useAppSelector(localizationSelector);

  // Define Zod schema
  const schema = z
    .object({
      companyName: z.string().min(1, t('signup.form.company_name_required')),
      companyEmail: z.string().min(1, t('signup.form.company_email_required')),
      vatNumber: z.string().optional(),
      dialCode: z.string().optional(),
      phoneNumber: z.string().optional(),
      street: z.string().optional(),
      zipCode: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().min(1, t('signup.form.country_required'))
    })
    .superRefine((data, ctx) => {
      if (data.street && !data.zipCode) {
        ctx.addIssue({
          path: ['zipCode'],
          code: 'custom',
          message: 'input.required'
        });
      }
      if (data.street && !data.city) {
        ctx.addIssue({
          path: ['city'],
          code: 'custom',
          message: 'input.required'
        });
      }
    });

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    setValue,
    control
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      companyName: data.companyName,
      vatNumber: data.vatNumber,
      companyEmail: data.companyEmail,
      country: data.country,
      dialCode: data.companyPhoneNumber.dialCode,
      phoneNumber: data.companyPhoneNumber.number,
      street: data.address.street ?? '',
      zipCode: data.address.zipCode ?? '',
      city: data.address.city ?? '',
      state: data.address.state ?? ''
    },
    resolver: zodResolver(schema)
  });

  // State
  const { email, accountStatus } = data;

  // Watch - using useWatch for React 19 compiler compatibility
  const [watchCountry, watchStreet] = useWatch({
    control,
    name: ['country', 'street']
  });

  // Reset inputfield
  const reset = useCallback(
    (name: any) => {
      setValue(name, '');
      clearErrors(name);
    },
    [setValue, clearErrors]
  );

  // Fetch captcha
  useEffect(() => {
    dispatch(fetchCaptcha(email));
  }, [dispatch, email]);

  // Reset zip code and city
  useEffect(() => {
    if (!watchStreet) {
      setValue('zipCode', '');
      setValue('city', '');
      setValue('state', '');
      clearErrors(['zipCode', 'city', 'state']);
    }
  }, [setValue, clearErrors, watchStreet]);

  // Reset captcha code
  useEffect(() => {
    if (error) {
      dispatch(fetchCaptcha(email));
    }
    if (error?.code === 'captcha.not.found') {
      reset('captchaCode');
    }
  }, [dispatch, reset, email, error]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const newAddress = {
        street: values.street,
        zipCode: values.zipCode,
        city: values.city,
        state: values.state,
        country: ''
      };

      if (!allKeysAreEmptyStrings(newAddress)) {
        newAddress.country = values.country;
      }

      dispatch(
        submitCompanyDetails({
          companyName: values.companyName,
          vatNumber: values.vatNumber,
          companyEmail: values.companyEmail,
          address: newAddress,
          country: values.country,
          language: languageCode ?? '',
          phoneNumber: {
            dialCode: values.dialCode,
            number: values.phoneNumber
          }
        })
      );
      navigate(routes.SIGNUP_COMPANY_MAIN_CONTACT);
    },
    [dispatch, navigate, languageCode]
  );

  // Cancel company details
  const onBack = useCallback(() => {
    dispatch(resetCompanyDetails());
    navigate(routes.SIGNUP);
  }, [dispatch, navigate]);

  // On dial code change
  const onDialCodeChange = useCallback(
    (value: string) => setValue('dialCode', value),
    [setValue]
  );

  // Set default country code
  const defaultCountryCode = useMemo(() => getBrowserCountryCode(), []);

  // Redirect to login if auth is required and user is not authenticated
  if (hasLoggedIn && !session.authenticated) {
    return <Navigate to={routes.LOGIN} />;
  }

  return (
    <PageImageWrapper alignTop>
      <SeoHelmet title="signup.company_details.meta_title" />
      {isCredentialsCompleted && (
        <BackButton onClick={onBack}>{t('productinfo.btn_back')}</BackButton>
      )}
      <PageHeader
        title={t('signup.company_details.title')}
        subtitle={t('signup.company_details.subtitle')}
        image={companyIcon}
        imageAlt="company"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          name="companyName"
          register={register('companyName')}
          description="signup.form.label_company_name"
          error={errors.companyName}
          reset={reset}
        />
        <SelectField
          name="country"
          register={register('country')}
          setValue={setValue}
          clearErrors={clearErrors}
          placeholder={t('signup.form.label_country_prompt')}
          description="signup.form.label_country"
          error={errors.country}
          defaultValue={defaultCountryCode}
          options={countryOptions}
        />
        {!accountStatus.customerAccountExists && (
          <InputField
            name="vatNumber"
            register={register('vatNumber')}
            description="signup.form.label_vat_number"
            error={errors.vatNumber}
            reset={reset}
          />
        )}
        <InputField
          name="companyEmail"
          register={register('companyEmail')}
          description="signup.form.label_company_email"
          error={errors.companyEmail}
          reset={reset}
        />
        <PhoneNumberField
          description="signup.form.label_phone_number"
          numberInputRef={register('phoneNumber')}
          dialCodeInputRef={register('dialCode')}
          onDialCodeChange={onDialCodeChange}
          defaultCountry={watchCountry}
          error={errors.phoneNumber}
        />
        <InputField
          name="street"
          register={register('street')}
          description="signup.form.label_address"
          error={errors.street}
          reset={reset}
        />
        <AnimatedFormGroup isOpen={!!watchStreet}>
          <InputField
            name="zipCode"
            register={register('zipCode')}
            description="signup.form.label_zipCode"
            error={errors.zipCode}
            reset={reset}
          />
          <InputField
            name="city"
            register={register('city')}
            description="signup.form.label_city"
            error={errors.city}
            reset={reset}
          />
          {watchCountry === 'US' && (
            <InputField
              name="state"
              register={register('state')}
              description="signup.form.label_state"
              error={errors.state}
              reset={reset}
            />
          )}
        </AnimatedFormGroup>
        <ButtonContainer>
          <Button submit fullWidth>
            {t('button.continue')}
          </Button>
          {isCredentialsCompleted && (
            <Button
              onClick={onBack}
              color="background"
              textColor="primary"
              border
            >
              {t('button.back')}
            </Button>
          )}
        </ButtonContainer>
      </Form>
    </PageImageWrapper>
  );
};
